import { toast } from "react-toastify";
import { BaseApplicationService } from "../../domain/base/base-application-service";
import apiClient from "../../apis/api-client";
import { default as tenantManagementClient } from "../../apis/tenant-management/tenant-management-client";
import { getFriendlyErrorMessage } from "../../util/error-message-mapper";
import {
  defaultShortcut,
  isShortcut,
  Shortcut,
  ShortcutDetail,
} from "../../domain/ttzk-portal";
import { reportTtzkPortalRelatedError } from "../../domain/ttzk-portal/util/error";
import { TtzkPortalR } from "../../../components/pages/ttzk-portal/route";
import { CloudGovApiError } from "../../domain/api-error";

export type ShortcutUpsertContextProps = {
  shortcut: Shortcut | ShortcutDetail;
  fetchShortcutById: (shortcutId: string) => Promise<void>;
  onSave: () => Promise<void>;
  onChange: (shortcut: ShortcutDetail) => void;
  isUpdating: boolean;
  onFileAttached: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;

  organizationSlug?: string;
  setOrganization: (id: string) => void;
};

export class TtzkPortalShortcutUpsertService extends BaseApplicationService<ShortcutUpsertContextProps> {
  state = {
    shortcut: defaultShortcut(),
    onSave: this.handleSave.bind(this),
    onChange: this.handleChange.bind(this),
    onFileAttached: this.handleFileAttached.bind(this),
    fetchShortcutById: this.fetchShortcutById.bind(this),
    isUpdating: false,

    organizationSlug: undefined,
    setOrganization: this.setOrganization.bind(this),
  };

  async handleSave() {
    const { shortcut } = this.state;
    const isUpdate = isShortcut(shortcut);
    const operationName = isUpdate ? "更新" : "作成";
    try {
      if (isUpdate) {
        const { data } = await apiClient.ttzkPortal.updateShortcut(shortcut);
        toast.success("ショートカット情報を更新しました。");
        this.history.push(
          TtzkPortalR.ShortcutDetail.link({ shortcutId: data.id }),
        );
      } else {
        const { data } = await apiClient.ttzkPortal.createShortcut(shortcut);
        this.setState({
          shortcut: data,
        });
        toast.success("ショートカットを新規作成しました。");
        this.history.push(
          TtzkPortalR.ShortcutDetail.link({ shortcutId: data.id }),
        );
      }
    } catch (error) {
      reportTtzkPortalRelatedError(error);
      if (CloudGovApiError.isTtzkPortalShortcutMaxCountError(error)) {
        toast.error("ショートカットの公開上限を超過しました。");
        return;
      }
      const friendlyMessage = getFriendlyErrorMessage(error);
      toast.error(
        `ショートカットの${operationName}に失敗しました: ${friendlyMessage}`,
      );
    }
  }

  async fetchShortcutById(shortcutId: string) {
    try {
      const { data } = await apiClient.ttzkPortal.findShortcutById(shortcutId);
      this.setState({
        shortcut: data,
      });
    } catch (error) {
      reportTtzkPortalRelatedError(error);
      const friendlyMessage = getFriendlyErrorMessage(error);
      toast.error(`ショートカットの取得に失敗しました: ${friendlyMessage}`);
    }
  }

  async handleChange(shortcut: ShortcutDetail) {
    if (!shortcut) {
      return;
    }

    this.setState({
      shortcut: shortcut,
    });
  }

  async setOrganization(organizationId: string) {
    try {
      const { data } =
        await tenantManagementClient.fetchOrganization(organizationId);
      this.setState({
        organizationSlug: data.slug,
      });
    } catch (error) {
      reportTtzkPortalRelatedError(error);
      const friendlyMessage = getFriendlyErrorMessage(error);
      toast.error(`組織データの取得に失敗しました: ${friendlyMessage}`);
    }
  }

  async handleFileAttached(event: React.ChangeEvent<HTMLInputElement>) {
    const { organizationSlug } = this.state;
    if (!organizationSlug) {
      return;
    }

    const { files } = event.target;
    if (!files || !files[0]) {
      toast.error("ファイルが見つかりません。");
      return;
    }

    this.setState({ isUpdating: true });
    const { shortcut } = this.state;
    try {
      const res = await this.uploadFile(files[0], organizationSlug);
      const { path } = res;
      shortcut.imageUrl = path;
      toast.success("ファイルをアップロードしました。");
    } catch (error) {
      reportTtzkPortalRelatedError(error);
      const friendlyMessage = getFriendlyErrorMessage(error);
      toast.error(`ファイルをアップロードできませんでした: ${friendlyMessage}`);
    }

    this.setState({
      shortcut: shortcut,
      isUpdating: false,
    });
  }

  private async uploadFile(file: File, organizationSlug: string) {
    return (
      await apiClient.ttzkPortal.uploadFileWithMultipart({
        file,
        json: {
          name: file.name,
          type: file.type,
          organizationSlug,
        },
      })
    ).data;
  }
}
